/**
 * Single source of truth for Free + Pro plan content, used by both the
 * authenticated upgrade screen (`src/pages/Upgrade.jsx`) and the public
 * pricing page (`src/pages/Pricing.jsx`).
 *
 * Marketing prices live here. Real chargeable prices are owned by RevenueCat
 * - at purchase time the Upgrade screen reads `getOfferings()` and uses
 * those for the actual charge. The values below are what we *advertise*.
 * Cross-check against the RC dashboard when the marketing copy lands; an
 * explicit "Local prices may vary at checkout" footnote in the UI keeps
 * users honest if there's drift.
 *
 * USD + EUR amounts are PPP-aligned and preserve the ~50% annual discount
 * the GBP tier offers. Adjust to match RC reality if it differs.
 */

export const FREE_LIMITS = {
  friends: 8,
  groups: 2,
  membersPerGroup: 5,
  // Planned rounds and Smart Schedule runs are no longer capped on free. On
  // iOS the free tier is monetised by a rewarded ad-gate (1 free, then watch
  // an ad or go Pro) rather than a hard limit; web free users are unlimited.
}

export const PRO_FEATURES = [
  'Ad-free experience',
  'Unlimited friends (free: 8)',
  'Unlimited groups (free: 2)',
  'Unlimited members per group (free: 5 members per group)',
  'Priority support',
]

export const FREE_FEATURES = [
  'Up to 8 friends',
  'Up to 2 groups',
  'Up to 5 members per group',
  'Unlimited planned rounds',
  'Unlimited Smart Schedule runs',
  'Outlook, Google & ICS calendar sync',
  'Weather-scored tee times',
  'Live tee-time lookup across 20,000+ courses',
]

// Currency symbols + per-period prices. Annual prices already include the
// roughly 50% discount vs paying monthly all year. Update both halves
// together when re-pricing - `savingsLabel` is computed in-place to match.
export const CURRENCIES = {
  gbp: {
    code: 'GBP',
    symbol: '£',
    label: 'GBP',
    free: { monthly: 0, annual: 0 },
    pro: { monthly: 2.49, annual: 14.99 },
  },
  usd: {
    code: 'USD',
    symbol: '$',
    label: 'USD',
    free: { monthly: 0, annual: 0 },
    pro: { monthly: 2.99, annual: 17.99 },
  },
  eur: {
    code: 'EUR',
    symbol: '€',
    label: 'EUR',
    free: { monthly: 0, annual: 0 },
    pro: { monthly: 2.99, annual: 16.99 },
  },
}

export const DEFAULT_CURRENCY = 'gbp'

/**
 * Pick a currency from a browser locale string like `navigator.language`.
 * Falls back to `DEFAULT_CURRENCY` for anything we don't recognise. Keeps
 * the mapping intentionally simple - we have three currencies, not a global
 * locale matrix.
 */
export const currencyFromLocale = (locale) => {
  if (!locale || typeof locale !== 'string') return DEFAULT_CURRENCY
  const lower = locale.toLowerCase()
  // English-speaking countries that price in USD.
  if (
    lower.startsWith('en-us') ||
    lower.startsWith('en-ca') ||
    lower.startsWith('en-au') ||
    lower.startsWith('en-nz')
  ) {
    return 'usd'
  }
  // Eurozone-aligned locales.
  if (
    lower.startsWith('de') ||
    lower.startsWith('fr') ||
    lower.startsWith('es') ||
    lower.startsWith('it') ||
    lower.startsWith('nl') ||
    lower.startsWith('pt') ||
    lower.startsWith('fi') ||
    lower.startsWith('el') ||
    lower.startsWith('ga') ||
    lower.startsWith('en-ie')
  ) {
    return 'eur'
  }
  return DEFAULT_CURRENCY
}

/**
 * Format a price as a display string. Trims trailing `.00` for round numbers
 * so "£0" reads cleanly. Two decimals for everything else.
 */
export const formatPrice = (amount, currencyKey) => {
  const c = CURRENCIES[currencyKey] || CURRENCIES[DEFAULT_CURRENCY]
  if (amount === 0) return `${c.symbol}0`
  const rounded = Number.isInteger(amount) ? `${amount}` : amount.toFixed(2)
  return `${c.symbol}${rounded}`
}

/**
 * Annual-vs-monthly savings as a percentage string (e.g. "Save 50%"). Uses
 * the configured annual price vs 12 × monthly for the same currency.
 * Returns null if the annual price doesn't actually save anything.
 */
export const annualSavingsLabel = (currencyKey) => {
  const c = CURRENCIES[currencyKey] || CURRENCIES[DEFAULT_CURRENCY]
  const monthlyTotal = c.pro.monthly * 12
  if (!monthlyTotal || c.pro.annual >= monthlyTotal) return null
  const pct = Math.round((1 - c.pro.annual / monthlyTotal) * 100)
  return `Save ${pct}%`
}
